import { Badge, Button, Input, Label, MoneyInput } from '@estac1/ui';
import { useEffect, useId, useState } from 'react';

interface NumberFieldProps {
  label: string;
  hint?: string;
  value: number;
  disabled: boolean;
  onSave: (value: number) => void;
  unit?: string;
  min?: number;
  max?: number;
}

/** A number input that persists on blur only when the value actually changed. */
export const NumberField = ({
  label,
  hint,
  value,
  disabled,
  onSave,
  unit = 'dias',
  min = 0,
  max = 3650,
}: NumberFieldProps) => {
  const id = useId();
  const [local, setLocal] = useState(String(value));
  useEffect(() => {
    setLocal(String(value));
  }, [value]);

  const commit = () => {
    const n = Number.parseInt(local, 10);
    const clamped = Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : value;
    if (clamped !== value) onSave(clamped);
    setLocal(String(clamped));
  };

  return (
    <div>
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <div className="mt-1 flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          value={local}
          disabled={disabled}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          className="w-24"
        />
        <span className="text-xs text-muted-fg">{unit}</span>
      </div>
      {hint && <p className="mt-1 text-xs text-muted-fg">{hint}</p>}
    </div>
  );
};

interface FlagRowProps {
  label: string;
  hint: string;
  enabled: boolean;
  pending: boolean;
  onToggle: (value: boolean) => void;
}

export const FlagRow = ({ label, hint, enabled, pending, onToggle }: FlagRowProps) => (
  <div className="flex items-center justify-between gap-4 border-b border-border pb-3 last:border-0 last:pb-0">
    <div>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium">{label}</p>
        <Badge variant={enabled ? 'success' : 'default'} className="text-xs">
          {enabled ? 'Ativo' : 'Desligado'}
        </Badge>
      </div>
      <p className="text-xs text-muted-fg">{hint}</p>
    </div>
    <Button
      variant={enabled ? 'outline' : 'primary'}
      size="sm"
      disabled={pending}
      onClick={() => onToggle(!enabled)}
    >
      {enabled ? 'Desligar' : 'Ligar'}
    </Button>
  </div>
);

interface ValetPriceFieldProps {
  label: string;
  valueCents: number;
  disabled: boolean;
  onSave: (cents: number) => void;
}

/** Money input que persiste no blur, só quando o valor mudou (padrão do NumberField). */
export const ValetPriceField = ({ label, valueCents, disabled, onSave }: ValetPriceFieldProps) => {
  const [local, setLocal] = useState(valueCents);
  useEffect(() => {
    setLocal(valueCents);
  }, [valueCents]);
  return (
    <div>
      <p className="mb-1 text-xs text-muted-fg">{label}</p>
      <MoneyInput
        aria-label={label}
        value={local}
        disabled={disabled}
        onChange={setLocal}
        onBlur={() => {
          if (local !== valueCents) onSave(local);
        }}
      />
    </div>
  );
};
